import React from "react"
import { Image, StyleSheet, View } from "@react-pdf/renderer"
import {
  generateCodeBarAsBase64,
  type CodeBarFormat,
  type CodeBarOptions,
} from "./CodeBarGenerator"

type ViewBaseProps = React.ComponentProps<typeof View>

interface CodeBarProps extends Omit<ViewBaseProps, "style">, Omit<CodeBarOptions, "value"> {
  value: string
  width?: number // ancho renderizado del <Image> en el PDF
  height?: number // alto renderizado del <Image> en el PDF
  style?: any
  format?: CodeBarFormat
  /**
   * Factor por el que se multiplican el width/height internos con los que
   * JsBarcode genera el canvas (por defecto width: 2, height: 100), antes
   * de escalarlo al tamaño final del <Image> en el PDF. Subirlo genera un
   * PNG de mayor resolución, evitando que se vea pixelado al imprimirse o
   * hacer zoom. Por ejemplo, resolutionScale={3} genera el canvas al
   * triple de resolución. Default: 3.
   */
  resolutionScale?: number
}

const styles = StyleSheet.create({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: 10,
  },
})

const CodeBar: React.FC<CodeBarProps> = ({
  value,
  format = "CODE128",
  width = 200,
  height = 80,
  style,
  displayValue = true,
  text,
  fontOptions,
  fontSize,
  textMargin,
  textAlign,
  textPosition,
  background = "#ffffff",
  lineColor = "#000000",
  margin,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  autoFixChecksum = false,
  resolutionScale = 3,
  ...rest
}) => {
  // Se pasa como factory function: `@react-pdf/renderer` acepta que `src` sea
  // una función (la invoca y espera su resultado) o directamente una
  // promesa — ver `resolveSource` en `@react-pdf/layout` — mismo enfoque que
  // ChartJS/QR. Nada de useState/useEffect/loading-state acá: ese patrón es
  // para un ciclo de vida de UI en el browser (montaje, re-render por
  // interacción), pero `generatePDF` corre en el backend (incluso en un
  // worker thread) haciendo un único render de punta a punta, así que lo
  // único que hace falta es resolver el data URL antes de serializar el PDF.
  const resolveCodeBarSrc = async (): Promise<string> =>
    generateCodeBarAsBase64({
      value,
      format,
      // width/height acá controlan el módulo/alto internos de JsBarcode, no
      // el tamaño final del <Image> (ese lo define el width/height del
      // componente, más abajo). Se multiplican por resolutionScale para que
      // el canvas nazca en mayor resolución y no se vea pixelado al
      // escalarlo/imprimirlo, sin cambiar el tamaño final en el PDF.
      width: 2 * resolutionScale,
      height: 100 * resolutionScale,
      displayValue,
      text,
      fontOptions,
      // Si no se pasa fontSize/textMargin explícito, se escalan junto con
      // las barras (mismos defaults que usa JsBarcode: fontSize 20,
      // textMargin 2) para que el texto no quede desproporcionadamente
      // chico frente a un canvas generado en mayor resolución.
      fontSize: fontSize ?? 20 * resolutionScale,
      textMargin: textMargin ?? 2 * resolutionScale,
      textAlign,
      textPosition,
      background,
      lineColor,
      margin,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      autoFixChecksum,
    })

  return (
    <View style={[styles.container, style]} {...rest}>
      <Image src={resolveCodeBarSrc} style={{ width, height }} cache={false} />
    </View>
  )
}

export default CodeBar